import { useEffect } from "react";
import type { QueueItem, Status } from "../lib/api";
import { USE } from "./Inspector";
import { Thumb } from "./Thumb";

// The shared queue. Grouped by the folder each structure came from -- a
// pre-sorted kit stays sorted -- with a thumbnail per row, because a list of a
// thousand names says nothing about what the builds actually are.

const ICON: Record<Status, string> = {
  pending: "·",
  approved: "✓",
  rejected: "✕",
  later: "…",
};

export function Queue(props: {
  groups: [string, QueueItem[]][];
  currentId: number | null;
  filter: Status | "";
  placement: string;
  use: string;
  sort: string;
  search: string;
  me: string;
  busy: boolean;
  onFilter: (s: Status | "") => void;
  onPlacement: (p: string) => void;
  onUse: (u: string) => void;
  onSort: (s: string) => void;
  onSearch: (q: string) => void;
  onPick: (id: number) => void;
  onClaim: () => void;
}) {
  const flat = props.groups.flatMap(([, rows]) => rows);
  const total = flat.length;

  // Up/down walk the queue in the order it is shown -- with a thousand
  // structures, reaching for the mouse for every row is the slow part.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      const at = flat.findIndex((r) => r.id === props.currentId);
      const next =
        e.key === "ArrowDown"
          ? Math.min(flat.length - 1, at + 1)
          : Math.max(0, at - 1);
      if (flat[next] && flat[next].id !== props.currentId)
        props.onPick(flat[next].id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flat, props]);

  return (
    <aside className="queue">
      <button className="primary" onClick={props.onClaim} disabled={props.busy}>
        Nächste freie holen <kbd>Strg</kbd>+<kbd>N</kbd>
      </button>

      <div className="filters">
        <select
          value={props.filter}
          onChange={(e) => props.onFilter(e.target.value as Status | "")}
        >
          <option value="">alle</option>
          <option value="pending">offen</option>
          <option value="approved">übernommen</option>
          <option value="rejected">aussortiert</option>
          <option value="later">später</option>
        </select>
        <select
          value={props.placement}
          onChange={(e) => props.onPlacement(e.target.value)}
          title="Umgebung"
        >
          <option value="">jede Umgebung</option>
          <option value="0">Oberfläche</option>
          <option value="1">unterirdisch</option>
          <option value="2">schwebend</option>
          <option value="3">im Wasser</option>
        </select>
        <select
          value={props.use}
          onChange={(e) => props.onUse(e.target.value)}
          title="Verwendung"
        >
          <option value="">jede Art</option>
          {USE.map((u, i) => (
            <option key={u} value={i}>
              {u}
            </option>
          ))}
        </select>
        <select
          value={props.sort}
          onChange={(e) => props.onSort(e.target.value)}
          title="Sortierung"
        >
          <option value="">nach Ordner</option>
          <option value="name">nach Name</option>
          <option value="status">nach Stand</option>
        </select>
      </div>

      <input
        type="search"
        placeholder="Name oder Ordner filtern…"
        value={props.search}
        onChange={(e) => props.onSearch(e.target.value)}
      />

      <div className="tree">
        {props.groups.map(([folder, rows]) => {
          const done = rows.filter((r) => r.status !== "pending").length;
          return (
            <section key={folder}>
              <h3>
                {folder} <span>{done}/{rows.length}</span>
              </h3>
              <ul className="withthumbs">
                {rows.map((r) => {
                  const mine = r.claimedBy === props.me;
                  const takenByOther = r.claimedBy && !mine;
                  return (
                    <li
                      key={r.id}
                      className={[
                        r.status,
                        r.id === props.currentId ? "active" : "",
                        takenByOther ? "taken" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => props.onPick(r.id)}
                      title={
                        takenByOther
                          ? "jemand anderes bearbeitet das gerade"
                          : r.folder
                      }
                    >
                      <Thumb id={r.id} />
                      <span className="label">
                        <span className="icon">{ICON[r.status]}</span>
                        {r.name}
                        {r.use !== 0 && (
                          <span className="rolemark" title={USE[r.use]}>
                            {r.use === 1 ? "⌇" : "◆"}
                          </span>
                        )}
                        {r.geometryEdited && <span className="edited">✎</span>}
                        {takenByOther && <span className="lock">🔒</span>}
                        {r.role !== 0 && (
                          <span
                            className="rolemark"
                            title={
                              (r.role === 1 ? "Anker" : "Mitglied") +
                              " der Siedlung „" +
                              (r.settlement || "ohne Namen") +
                              "“"
                            }
                          >
                            {r.role === 1 ? "⚓" : "▪"}
                          </span>
                        )}
                        {r.group && <span className="grp" title="Teil einer Variantengruppe">◇</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
        {total === 0 && <p className="empty">Nichts passt zu diesem Filter.</p>}
      </div>
    </aside>
  );
}
